import { useState, useEffect, useCallback } from 'react';
import { api, API } from '../App';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, Cell
} from 'recharts';
import { Download, ClipboardList, CheckSquare, TrendingUp, Layers, Building2 } from 'lucide-react';

const PRESETS = [
  { label: 'Bugün', days: 0 },
  { label: 'Dün', days: 1 },
  { label: 'Son 7 Gün', days: 7 },
  { label: 'Son 30 Gün', days: 30 },
  { label: 'Bu Ay', key: 'thisMonth' },
  { label: 'Geçen Ay', key: 'lastMonth' },
  { label: 'Bu Yıl', key: 'thisYear' },
  { label: 'Özel', key: 'custom' },
];

function getDateRange(preset) {
  const now = new Date();
  const fmt = d => d.toISOString().slice(0, 10);
  if (preset.key === 'custom') return null;
  if (preset.key === 'thisMonth') return { baslangic: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), bitis: fmt(now) };
  if (preset.key === 'lastMonth') {
    return { baslangic: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)), bitis: fmt(new Date(now.getFullYear(), now.getMonth(), 0)) };
  }
  if (preset.key === 'thisYear') return { baslangic: `${now.getFullYear()}-01-01`, bitis: fmt(now) };
  if (preset.days === 0) return { baslangic: fmt(now), bitis: fmt(now) };
  if (preset.days === 1) { const d = new Date(now); d.setDate(d.getDate() - 1); return { baslangic: fmt(d), bitis: fmt(d) }; }
  const start = new Date(now); start.setDate(start.getDate() - preset.days);
  return { baslangic: fmt(start), bitis: fmt(now) };
}

const PUAN_RENK = (p) => p >= 4 ? '#16a34a' : p >= 3 ? '#f59e0b' : '#ef4444';

/* Küçük stat kartı */
function StatKart({ label, value, color, sub }) {
  return (
    <div className="card-stat" style={{ padding: 18 }}>
      <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || 'var(--blue)' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* Kat bazlı bar grafik */
function KatGrafik({ data, dataKey, label, color }) {
  if (!data?.length) return (
    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--gray-400)', fontSize: 13 }}>
      Kat verisi bulunamadı. Lokasyonlara kat atandığında burada görünecek.
    </div>
  );
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 42)}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis type="number" tick={{ fontSize: 11 }} />
        <YAxis dataKey="kat" type="category" tick={{ fontSize: 11 }} width={130} />
        <Tooltip
          formatter={(val, name) => [val, name]}
          labelFormatter={l => `Kat: ${l}`}
        />
        <Bar dataKey={dataKey} name={label} radius={[0, 5, 5, 0]}>
          {data.map((_, i) => <Cell key={i} fill={color} fillOpacity={0.85 - i * 0.05} />)}
        </Bar>
        {data[0]?.ort_puan !== undefined && (
          <Bar dataKey="ort_puan" name="Ort. Puan" fill="#f59e0b" radius={[0, 5, 5, 0]} />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function Raporlar() {
  const [tab, setTab] = useState('anket');
  const [preset, setPreset] = useState(PRESETS[2]);
  const [customRange, setCustomRange] = useState({ baslangic: '', bitis: '' });

  // Filtreler
  const [binalar, setBinalar] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [katlar, setKatlar] = useState([]);
  const [binaId, setBinaId] = useState('');
  const [katId, setKatId] = useState('');
  const [lokasyonId, setLokasyonId] = useState('');

  const [anketData, setAnketData] = useState(null);
  const [clData, setClData] = useState(null);
  const [karsilData, setKarsilData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getRange = () => preset.key === 'custom' ? customRange : (getDateRange(preset) || { baslangic: '', bitis: '' });

  const load = useCallback(async () => {
    const range = getRange();
    setLoading(true);
    try {
      const params = { ...range, lokasyon_id: lokasyonId, bina_id: binaId, kat_id: katId };
      if (tab === 'anket') {
        const { data } = await api.get('/raporlar/anket', { params });
        setAnketData(data);
      } else if (tab === 'checklist') {
        const { data } = await api.get('/raporlar/checklist', { params });
        setClData(data);
      } else {
        const { data } = await api.get('/raporlar/karsilastirmali', { params: range });
        setKarsilData(data);
      }
    } catch { toast.error('Rapor yüklenemedi'); }
    finally { setLoading(false); }
  }, [tab, preset, customRange, lokasyonId, binaId, katId]);

  useEffect(() => {
    api.get('/binalar').then(r => setBinalar(r.data)).catch(() => {});
    api.get('/lokasyonlar').then(r => setLokasyonlar(r.data)).catch(() => {});
  }, []);

  const handleBinaChange = async (id) => {
    setBinaId(id); setKatId(''); setLokasyonId('');
    if (id) {
      try { const { data } = await api.get(`/binalar/${id}/katlar`); setKatlar(data); }
      catch { setKatlar([]); }
    } else { setKatlar([]); }
  };

  useEffect(() => { load(); }, [tab, preset]);

  const handleExport = async (tip, format) => {
    const range = getRange();
    const params = new URLSearchParams({ tip, ...range, ...(lokasyonId && { lokasyon_id: lokasyonId }), ...(binaId && { bina_id: binaId }), ...(katId && { kat_id: katId }) });
    const a = document.createElement('a');
    a.href = `${API}/raporlar/export/${format}?${params}`;
    a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Filtreli lokasyonlar
  const filteredLoks = lokasyonlar.filter(l =>
    (!binaId || String(l.bina_id) === binaId) &&
    (!katId || String(l.kat_id) === katId)
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Raporlar</h1>
          <p className="page-subtitle">İstatistik ve analizler</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => handleExport(tab === 'checklist' ? 'checklist' : 'anket', 'excel')}>
            <Download size={14} /> Excel
          </button>
          {tab === 'anket' && (
            <button className="btn btn-secondary btn-sm" onClick={() => handleExport('anket', 'pdf')}>
              <Download size={14} /> PDF
            </button>
          )}
        </div>
      </div>

      {/* Zaman presetleri */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {PRESETS.map(p => (
          <button key={p.label} onClick={() => setPreset(p)}
            className={`btn btn-sm ${preset.label === p.label ? 'btn-primary' : 'btn-secondary'}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom range */}
      {preset.key === 'custom' && (
        <div className="card filter-card" style={{ padding: 16, marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="input-group" style={{ margin: 0 }}>
            <label className="input-label">Başlangıç</label>
            <input className="input" type="date" value={customRange.baslangic} onChange={e => setCustomRange({ ...customRange, baslangic: e.target.value })} />
          </div>
          <div className="input-group" style={{ margin: 0 }}>
            <label className="input-label">Bitiş</label>
            <input className="input" type="date" value={customRange.bitis} onChange={e => setCustomRange({ ...customRange, bitis: e.target.value })} />
          </div>
          <button className="btn btn-primary" onClick={load}>Uygula</button>
        </div>
      )}

      {/* ── FİLTRELER ── */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Filtreler
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Bina */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 170 }}>
            <label style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Building2 size={11} /> Bina
            </label>
            <select className="input" value={binaId} onChange={e => handleBinaChange(e.target.value)}>
              <option value="">Tüm Binalar</option>
              {binalar.map(b => <option key={b.id} value={String(b.id)}>{b.ad}</option>)}
            </select>
          </div>

          {/* Kat */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
            <label style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Layers size={11} /> Kat / Servis
            </label>
            <select className="input" value={katId} onChange={e => { setKatId(e.target.value); setLokasyonId(''); }}
              disabled={!binaId && !katlar.length}>
              <option value="">Tüm Katlar</option>
              {katlar.map(k => <option key={k.id} value={String(k.id)}>{k.ad}</option>)}
            </select>
          </div>

          {/* Lokasyon */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
            <label style={{ fontSize: 11, color: 'var(--gray-500)' }}>Lokasyon</label>
            <select className="input" value={lokasyonId} onChange={e => setLokasyonId(e.target.value)}>
              <option value="">Tüm Lokasyonlar</option>
              {filteredLoks.map(l => (
                <option key={l.id} value={String(l.id)}>
                  {l.ad}{l.kat_adi ? ` (${l.kat_adi})` : ''}
                </option>
              ))}
            </select>
          </div>

          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={load}>
            Uygula
          </button>

          {(binaId || katId || lokasyonId) && (
            <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => {
              setBinaId(''); setKatId(''); setLokasyonId(''); setKatlar([]);
            }}>
              Temizle
            </button>
          )}
        </div>

        {/* Aktif filtre etiketleri */}
        {(binaId || katId || lokasyonId) && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {binaId && <span className="badge badge-blue"><Building2 size={10} style={{ marginRight: 3 }} />{binalar.find(b => String(b.id) === binaId)?.ad}</span>}
            {katId && <span className="badge badge-blue"><Layers size={10} style={{ marginRight: 3 }} />{katlar.find(k => String(k.id) === katId)?.ad}</span>}
            {lokasyonId && <span className="badge badge-gray">{lokasyonlar.find(l => String(l.id) === lokasyonId)?.ad}</span>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {[
          { key: 'anket', label: 'Anket İstatistik', icon: ClipboardList },
          { key: 'checklist', label: 'Checklist İstatistik', icon: CheckSquare },
          { key: 'karsilastirmali', label: 'Karşılaştırmalı', icon: TrendingUp },
        ].map(t => (
          <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <t.icon size={14} />{t.label}
          </div>
        ))}
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
        </div>
      )}

      {/* ── ANKET TAB ── */}
      {!loading && tab === 'anket' && anketData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="stats-grid-3">
            <StatKart label="Toplam Anket" value={anketData.ozet?.toplam} color="var(--blue)" />
            <StatKart label="Ortalama Puan" value={anketData.ozet?.ort_puan ? `${anketData.ozet.ort_puan} / 5` : '—'} color="#f59e0b" />
            <StatKart label="Düşük Puan (<3)" value={anketData.ozet?.dusuk_puan} color="#ef4444" />
          </div>

          {/* Günlük trend */}
          {anketData.gunluk?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>Günlük Anket & Puan Trendi</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={anketData.gunluk}>
                  <defs>
                    <linearGradient id="r1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="tarih" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={l => `Tarih: ${l}`} />
                  <Area type="monotone" dataKey="sayi" name="Anket" stroke="#3b82f6" strokeWidth={2} fill="url(#r1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* KAT BAZLI — yeni bölüm */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Layers size={16} color="var(--blue)" />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Kat / Servis Bazlı</h3>
              {!anketData.kat_bazli?.length && (
                <span className="badge badge-gray" style={{ marginLeft: 4 }}>Veri yok</span>
              )}
            </div>
            <KatGrafik
              data={anketData.kat_bazli}
              dataKey="anket_sayisi"
              label="Anket Sayısı"
              color="#6366f1"
            />
            {anketData.kat_bazli?.length > 0 && (
              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table>
                  <thead>
                    <tr>
                      <th><Layers size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Kat / Servis</th>
                      <th>Bina</th>
                      <th>Anket Sayısı</th>
                      <th>Ort. Puan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anketData.kat_bazli.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{r.kat}</td>
                        <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{r.bina || '—'}</td>
                        <td><span className="badge badge-blue">{r.anket_sayisi}</span></td>
                        <td>
                          {r.ort_puan
                            ? <span className="badge" style={{ background: PUAN_RENK(r.ort_puan) + '22', color: PUAN_RENK(r.ort_puan), fontWeight: 700 }}>
                                {Number(r.ort_puan).toFixed(1)} / 5
                              </span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Lokasyon bazlı */}
          {anketData.lokasyon_bazli?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>Lokasyon Bazlı</h3>
              <ResponsiveContainer width="100%" height={Math.max(200, anketData.lokasyon_bazli.slice(0, 12).length * 36)}>
                <BarChart data={anketData.lokasyon_bazli.slice(0, 12)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="lokasyon" type="category" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="anket_sayisi" name="Anket" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="ort_puan" name="Ort. Puan" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Soru analizi */}
          {anketData.sorular?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Soru Bazlı Analiz</h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Soru</th><th>Tip</th><th>Cevap</th><th>Ort. Yıldız</th><th>Evet</th><th>Hayır</th></tr>
                  </thead>
                  <tbody>
                    {anketData.sorular.map((s, i) => (
                      <tr key={i}>
                        <td style={{ maxWidth: 240, fontSize: 13 }}>{s.soru_metni}</td>
                        <td><span className="badge badge-gray">{s.soru_tipi}</span></td>
                        <td>{s.cevap_sayisi}</td>
                        <td>{s.ort_yildiz || '—'}</td>
                        <td style={{ color: '#16a34a', fontWeight: 600 }}>{s.evet_sayisi || '—'}</td>
                        <td style={{ color: '#dc2626', fontWeight: 600 }}>{s.hayir_sayisi || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CHECKLİST TAB ── */}
      {!loading && tab === 'checklist' && clData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="stats-grid-3">
            <StatKart label="Toplam Kontrol" value={clData.ozet?.toplam} color="var(--blue)" />
          </div>

          {/* KAT BAZLI */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Layers size={16} color="#10b981" />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Kat / Servis Bazlı</h3>
              {!clData.kat_bazli?.length && (
                <span className="badge badge-gray" style={{ marginLeft: 4 }}>Veri yok</span>
              )}
            </div>
            <KatGrafik
              data={clData.kat_bazli}
              dataKey="kontrol_sayisi"
              label="Kontrol Sayısı"
              color="#10b981"
            />
            {clData.kat_bazli?.length > 0 && (
              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table>
                  <thead>
                    <tr>
                      <th><Layers size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Kat / Servis</th>
                      <th>Bina</th>
                      <th>Kontrol Sayısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clData.kat_bazli.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{r.kat}</td>
                        <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{r.bina || '—'}</td>
                        <td><span className="badge badge-green">{r.kontrol_sayisi}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Personel bazlı */}
          {clData.personel_bazli?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>Personel Bazlı</h3>
              <ResponsiveContainer width="100%" height={Math.max(160, clData.personel_bazli.length * 36)}>
                <BarChart data={clData.personel_bazli} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="personel" type="category" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="kontrol_sayisi" name="Kontrol" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {clData.gunluk?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>Günlük Kontrol Trendi</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={clData.gunluk}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="tarih" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="sayi" name="Kontrol" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── KARŞILAŞTIRMALI TAB ── */}
      {!loading && tab === 'karsilastirmali' && karsilData && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Lokasyon Karşılaştırma</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--gray-600)' }}>Anket puanı, anket sayısı ve checklist kontrolü bir arada</p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lokasyon</th>
                  <th>Bina</th>
                  <th><Layers size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />Kat</th>
                  <th>Ort. Puan</th>
                  <th>Anket</th>
                  <th>Checklist</th>
                  <th>Personel</th>
                </tr>
              </thead>
              <tbody>
                {karsilData.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.lokasyon}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{r.bina || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--blue)' }}>{r.kat || '—'}</td>
                    <td>
                      {r.ort_puan
                        ? <span className="badge" style={{ background: PUAN_RENK(r.ort_puan) + '22', color: PUAN_RENK(r.ort_puan), fontWeight: 700 }}>
                            {Number(r.ort_puan).toFixed(1)} / 5
                          </span>
                        : <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>—</span>}
                    </td>
                    <td><span className="badge badge-blue">{r.anket_sayisi}</span></td>
                    <td><span className="badge badge-green">{r.checklist_sayisi}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--gray-600)', maxWidth: 180 }}>{r.personel_listesi || '—'}</td>
                  </tr>
                ))}
                {!karsilData.length && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Veri bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}